import { useReducer, useEffect } from 'react'
import api from '@api'
import { Order, IPageInfo, IBuildingTypeWithCompany } from '@api/types'
import { uniqueId } from 'lodash'
import { useShouldReload } from '@hooks/reloadContext'
import { IGridTableCell, GridTableStatusEnum } from '@ui'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  SORT,
  NEXT_PAGE,
  QUERY,
  ERROR,
  OPTIMISTIC_DELETE,
  TOGGLE_INCLUDE_OLD,
  RESET_FILTERS,
  FILTER,
}

export interface Filters {
  query: string
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: string
}

interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: string
}

interface LoadAction {
  type: Actions.LOAD
  reqId: string
  items: IGridTableCell<IBuildingTypeWithCompany>[]
  count: number
  pageInfo: IPageInfo
}

interface SortAction {
  type: Actions.SORT
  column: string
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

interface QueryAction {
  type: Actions.QUERY
  query: string
}

interface ErrorAction {
  type: Actions.ERROR
  reqId: string
}
interface OptimisticDeleteAction {
  type: Actions.OPTIMISTIC_DELETE
  ids: string[]
}

interface ToggleIncludeOldAction {
  type: Actions.TOGGLE_INCLUDE_OLD
}

interface ResetFiltersAction {
  type: Actions.RESET_FILTERS
}

interface ApplyFiltersAction {
  type: Actions.FILTER
  filters: Filters
}

export interface IDHash {
  [id: string]: boolean
}

type Action =
  | LoadAction
  | LoadStartAction
  | NextPageAction
  | SortAction
  | QueryAction
  | ErrorAction
  | LoadStartActionWithoutSkeleton
  | OptimisticDeleteAction
  | ToggleIncludeOldAction
  | ResetFiltersAction
  | ApplyFiltersAction

interface Sort {
  column: string
  order: Order
}

interface State {
  reqId: string
  status: GridTableStatusEnum
  count: number
  items: IGridTableCell<IBuildingTypeWithCompany>[]
  after: string | null
  pageInfo: IPageInfo
  sort: Sort
  first: number
  filters: Filters
}

export interface UseBuildingTypesHook extends State {
  sortBy: (column: string) => void
  nextPage: () => void
  optimisticDelete: (ids: string[]) => void
  resetFilters: () => void
  applyFilters: (filters: Filters) => void
  search: (query: string) => void
}

let persistedState: State = {
  reqId: uniqueId('request'),
  status: GridTableStatusEnum.LOADING,
  count: 0,
  first: PAGE_SIZE,
  items: [],
  after: null,
  sort: { column: 'displayName', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  filters: {
    query: '',
  },
}

interface GetVariablesFunc {
  sort: Sort
  after?: string | null
  first: number
  filters: Filters
}

export function getVariables({ sort, after, first, filters }: GetVariablesFunc) {
  return {
    after,
    orderBy: sort.column + '_' + sort.order,
    first,
    where: filters.query ? { displayName_contains: filters.query } : null,
  }
}

export default function useBuildingTypes(): UseBuildingTypesHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const shouldReload = useShouldReload()

  const fetchBuildingTypes =
    ({ showSkeleton, loadAll }: { showSkeleton: boolean; loadAll: boolean }) =>
    () => {
      const reqId = uniqueId('request')
      const variables = getVariables({
        after: loadAll ? null : state.after,
        sort: state.sort,
        first: loadAll ? state.first : PAGE_SIZE,
        filters: state.filters,
      })

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      api.building.buildingType.list(variables).then(({ buildingTypes, counters, pageInfo }) => {
        dispatch({
          type: Actions.LOAD,
          count: counters.total,
          items: buildingTypes.map((bt) => ({ data: bt })),
          pageInfo,
          reqId,
        })
      })
    }

  //api request without skeleton and with load only 25 items after cursor
  useEffect(fetchBuildingTypes({ showSkeleton: false, loadAll: false }), [state.after])

  //api request without skeleton and with load all items in state.first
  useEffect(fetchBuildingTypes({ showSkeleton: false, loadAll: true }), [
    state.sort,
    shouldReload.buildingTypes,
  ])

  //api request with skeleton and with load only 25 items after cursor
  useEffect(fetchBuildingTypes({ showSkeleton: true, loadAll: false }), [state.filters])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  return {
    ...state,
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
    },
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, column })
    },
    optimisticDelete: (ids: string[]) => {
      dispatch({ type: Actions.OPTIMISTIC_DELETE, ids })
    },
    resetFilters: () => {
      dispatch({
        type: Actions.RESET_FILTERS,
      })
    },
    applyFilters: (filters: Filters) => {
      dispatch({
        type: Actions.FILTER,
        filters,
      })
    },
    search: (query: string) => {
      dispatch({ type: Actions.FILTER, filters: { query } })
    },
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.LOADING,
      }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.IDLE,
      }

    case Actions.ERROR:
      return action.reqId === state.reqId ? { ...state, status: GridTableStatusEnum.IDLE } : state

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            status: GridTableStatusEnum.IDLE,
            count: action.count,
            items:
              state.status === GridTableStatusEnum.PAGING
                ? [...state.items, ...action.items]
                : action.items,
            pageInfo: action.pageInfo,
          }
        : state

    case Actions.SORT:
      return {
        ...state,
        status: GridTableStatusEnum.SORTING,
        pageInfo: { hasNextPage: false, endCursor: '' },
        sort: {
          column: action.column,
          order:
            state.sort.column === action.column
              ? state.sort.order === Order.ASC
                ? Order.DESC
                : Order.ASC
              : Order.ASC,
        },
      }

    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        status: GridTableStatusEnum.PAGING,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
      }

    case Actions.OPTIMISTIC_DELETE:
      return {
        ...state,
        items: state.items.filter((booking) => !action.ids.includes(booking.data.id)),
      }

    case Actions.RESET_FILTERS:
      return {
        ...state,
        filters: {
          query: '',
        },
      }

    case Actions.FILTER:
      return {
        ...state,
        filters: action.filters,
        pageInfo: { hasNextPage: false, endCursor: '' },
      }

    default:
      return state
  }
}
